/**
 * Core Pagination Component
 *
 * Standalone page navigation with page size selector, total count,
 * range display, and compact/full modes. Built on MUI Pagination.
 *
 * @example
 *   <Pagination page={1} pageSize={20} total={200} onChange={setPage} />
 *   <Pagination page={page} pageSize={size} total={total} onChange={setPage}
 *     onPageSizeChange={setSize} showPageSize showRange />
 */
import React from 'react';
import { Pagination as MuiPagination, Box, Typography } from '@mui/material';

export interface PaginationProps {
  /** Current page (1-indexed) */
  page: number;
  /** Items per page */
  pageSize: number;
  /** Total item count */
  total: number;
  /** Page change handler */
  onChange: (page: number) => void;
  /** Page size change handler */
  onPageSizeChange?: (pageSize: number) => void;
  /** Page size options */
  pageSizeOptions?: number[];
  /** Show page size selector */
  showPageSize?: boolean;
  /** Show "1-20 of 200" range */
  showRange?: boolean;
  /** Size */
  size?: 'small' | 'medium' | 'large';
  /** Shape */
  shape?: 'rounded' | 'circular';
  /** Variant */
  variant?: 'text' | 'outlined';
  /** Color */
  color?: 'primary' | 'secondary' | 'standard';
  /** Disabled */
  disabled?: boolean;
  className?: string;
}

export const Pagination: React.FC<PaginationProps> = ({
  page,
  pageSize,
  total,
  onChange,
  onPageSizeChange,
  pageSizeOptions = [10, 20, 50, 100],
  showPageSize = true,
  showRange = true,
  size = 'medium',
  shape = 'rounded',
  variant = 'outlined',
  color = 'primary',
  disabled = false,
  className = '',
}) => {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const rangeStart = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = Math.min(page * pageSize, total);

  return (
    <Box className={className} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
      {/* Left side — range + page size */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {showRange && (
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
            {rangeStart}–{rangeEnd} of <strong>{total}</strong>
          </Typography>
        )}
        {showPageSize && onPageSizeChange && (
          <select
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            disabled={disabled}
            className="bg-transparent border border-slate-200 dark:border-slate-700 rounded-lg px-2 py-1 text-xs text-slate-600 dark:text-slate-300"
          >
            {pageSizeOptions.map((s) => (
              <option key={s} value={s}>{s} / page</option>
            ))}
          </select>
        )}
      </Box>

      {/* Right side — page controls */}
      <MuiPagination
        count={totalPages}
        page={page}
        onChange={(_, p) => onChange(p)}
        size={size}
        shape={shape}
        variant={variant}
        color={color}
        disabled={disabled}
        showFirstButton
        showLastButton
      />
    </Box>
  );
};
export default Pagination;
